<template>
  <section ref="rootRef" data-testid="insights-perspective-reviews">
    <div class="grid grid-cols-1 gap-6 tablet:grid-cols-2" data-testid="insights-reviews-cards">
      <InsightsMetricCard
        :headline="t('Reviews.product.insights.reviews.yourScore.title')"
        :subheadline="t('Reviews.product.insights.reviews.yourScore.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.yourScore.tooltip')"
        data-testid="insights-reviews-card-your-score"
      >
        <div class="flex flex-col gap-6">
          <div>
            <InsightsHeroMetric
              :value="scoreHero.value"
              :trend="scoreHero.trend"
              :caption="scoreHero.caption"
              data-testid="insights-reviews-your-score-hero"
            />
            <div class="mt-2 flex items-center gap-2">
              <RatingStars :value="view.score.rating ?? undefined" :size="20" data-testid="insights-reviews-your-score-stars" />
              <span class="txt-label-s-regular text-subtle tabular-nums" data-testid="insights-reviews-your-score-count">
                {{ t('Reviews.product.insights.reviews.yourScore.reviews', { count: scoreCount.label }, scoreCount.value) }}
              </span>
            </div>
          </div>
          <InsightsDataState
            v-if="insightsLoading.history || history.state !== 'empty'"
            :loading="insightsLoading.history"
            :state="history.state"
            data-testid="insights-reviews-your-score-history"
          >
            <InsightsRatingLineChart
              v-if="tokens"
              :categories="history.weeks"
              :values="history.ratings"
              :counts="history.counts"
              :color="tokens.palette[0]"
              :count-color="tokens.axisLabel"
              :value-label="t('Reviews.product.insights.reviews.yourScore.series.rating')"
              :count-label="t('Reviews.product.insights.reviews.yourScore.series.reviews')"
              :locale="locale"
              data-testid="insights-reviews-your-score-chart"
              @hover="hoveredWeek = $event"
            />
          </InsightsDataState>
        </div>
      </InsightsMetricCard>

      <InsightsMetricCard
        :headline="t('Reviews.product.insights.reviews.distribution.title')"
        :subheadline="t('Reviews.product.insights.reviews.distribution.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.distribution.tooltip')"
        data-testid="insights-reviews-card-distribution"
      >
        <InsightsDataState :loading="insightsLoading.distribution" :state="distribution.state" data-testid="insights-reviews-distribution">
          <ul v-if="tokens" class="flex flex-col gap-4" data-testid="insights-reviews-distribution-chart">
            <li
              v-for="row in distribution.rows"
              :key="row.stars"
              class="flex items-center gap-3.5"
              :data-testid="`insights-reviews-distribution-row-${row.stars}`"
            >
              <span class="flex w-12 items-center gap-1.5 txt-label-m-semibold text-main">
                <StarIcon class="lucide-icon-s text-icon-main" aria-hidden="true" />
                {{ row.stars }}
              </span>
              <InsightsTrackBar
                class="h-4 min-w-0 flex-1"
                :value="shareOf(row.value)"
                :max="1"
                :color="tokens.palette[0]"
                :track-color="tokens.gridLine"
                :marker="lifetimeShare(row.stars)"
                :marker-color="tokens.dataLabel"
                :marker-label="lifetimeMarkerLabel(row.stars)"
                :data-testid="`insights-reviews-distribution-row-${row.stars}-bar`"
              />
              <span
                class="min-w-24 text-right txt-label-m-regular text-subtle tabular-nums"
                :data-testid="`insights-reviews-distribution-row-${row.stars}-value`"
              >
                <span class="txt-label-m-bold text-main">{{ formatPercent(shareOf(row.value)) }}</span>
                ({{ formatCount(row.value) }})
              </span>
            </li>
          </ul>
          <p
            v-if="view.lifetimeDistribution.total"
            class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 txt-label-s-regular text-subtle"
            data-testid="insights-reviews-distribution-legend"
          >
            <span class="flex items-center gap-1.5">
              <span class="h-2 w-3 rounded-sm bg-primary-main" aria-hidden="true" />
              {{ t('Reviews.product.insights.reviews.distribution.legend.timeframe') }}
            </span>
            <span class="flex items-center gap-1.5">
              <span class="h-3 w-0.5 bg-surface-inverse" aria-hidden="true" />
              {{ t('Reviews.product.insights.reviews.distribution.legend.lifetime') }}
            </span>
          </p>
        </InsightsDataState>
        <div
          v-if="distributionReadme"
          class="mt-4 flex items-start gap-2.5 rounded-2xl border border-subtle bg-surface-elevation-1 px-3.5 py-3"
          data-testid="insights-reviews-distribution-readme"
        >
          <InfoIcon class="mt-0.5 lucide-icon-s shrink-0 text-icon-primary" aria-hidden="true" />
          <p class="max-w-prose txt-body-s-regular text-pretty text-subtle">{{ distributionReadme }}</p>
        </div>
        <template v-if="!insightsLoading.distribution" #footer>
          <RouterLink
            class="btn-primary-color-s"
            :to="campaignsRoute"
            data-testid="insights-reviews-distribution-cta"
            @click="trackCampaignsCta('insights_reviews_distribution_reviews')"
          >
            {{ t('Reviews.product.insights.reviews.distribution.cta') }}
            <ArrowRightIcon class="lucide-icon-s" aria-hidden="true" />
          </RouterLink>
        </template>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.reviews.reviewCount.title')"
        :subheadline="t('Reviews.product.insights.reviews.reviewCount.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.reviewCount.tooltip')"
        data-testid="insights-reviews-card-review-count"
      >
        <div class="grid grid-cols-1 gap-6 desktop:grid-cols-2">
          <div class="flex flex-col gap-6">
            <div class="grid grid-cols-2 gap-6">
              <div data-testid="insights-reviews-total-reviews">
                <p class="txt-label-m-semibold text-subtle">{{ t('Reviews.product.insights.reviews.reviewCount.total') }}</p>
                <InsightsDataState
                  :loading="insightsLoading.totalReviews"
                  :state="insights.timeframe.totalReviews.state"
                  data-testid="insights-reviews-total-reviews-state"
                >
                  <InsightsHeroMetric
                    :value="insights.timeframe.totalReviews.value"
                    caption=""
                    data-testid="insights-reviews-total-reviews-hero"
                  />
                </InsightsDataState>
              </div>
              <div data-testid="insights-reviews-new-reviews">
                <p class="txt-label-m-semibold text-subtle">{{ t('Reviews.product.insights.reviews.reviewCount.new') }}</p>
                <InsightsDataState
                  :loading="insightsLoading.newReviews"
                  :state="insights.timeframe.newReviews.state"
                  data-testid="insights-reviews-new-reviews-state"
                >
                  <InsightsHeroMetric
                    :value="insights.timeframe.newReviews.value"
                    :trend="insights.timeframe.newReviews.delta ?? undefined"
                    :caption="t('Reviews.product.insights.reviews.reviewCount.newComparison')"
                    size="m"
                    data-testid="insights-reviews-new-reviews-hero"
                  />
                </InsightsDataState>
              </div>
            </div>
            <InsightsDataState
              :loading="insightsLoading.totalReviews"
              :state="insights.timeframe.benchmark.state"
              data-testid="insights-reviews-benchmark"
            >
              <InsightsBenchmarkChart
                v-if="tokens"
                :categories="reviewCountBenchmarkCategories"
                :values="reviewCountBenchmarkValues"
                :color="tokens.palette[3]"
                :highlight-color="tokens.palette[0]"
                :highlight-index="0"
                :marker="insights.timeframe.benchmark.median"
                :marker-label="reviewCountMedianLabel"
                :marker-color="tokens.palette[0]"
                grid-lines
                :locale="locale"
                data-testid="insights-reviews-benchmark-chart"
              />
            </InsightsDataState>
          </div>
          <InsightsDataState :loading="insightsLoading.badge" :state="insights.timeframe.badge.state" data-testid="insights-reviews-badge">
            <TopRatedBadgeBox
              :timeframe="currentQuarter"
              :review-count="insights.timeframe.badge.reviewCount"
              :minimum-review-count="insights.timeframe.badge.minimumReviewCount"
              :average-score="insights.timeframe.badge.averageScore"
              :eligible="insights.timeframe.badge.eligible"
              data-testid="insights-reviews-badge-box"
            />
          </InsightsDataState>
        </div>
      </InsightsMetricCard>

      <InsightsMetricCard
        :headline="t('Reviews.product.insights.reviews.quality.title')"
        :subheadline="t('Reviews.product.insights.reviews.quality.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.quality.tooltip')"
        data-testid="insights-reviews-card-quality"
      >
        <InsightsDataState :loading="insightsLoading.quality" :state="insights.quality.state" data-testid="insights-reviews-quality">
          <div v-if="tokens" class="flex flex-wrap justify-around gap-4">
            <InsightsProgressRing
              v-for="ring in insights.quality.rings"
              :key="ring.key"
              :value="ring.value"
              :label="ring.label"
              :caption="t(`Reviews.product.insights.reviews.quality.${ring.key}`)"
              :tooltip="t(`Reviews.product.insights.reviews.quality.tooltips.${ring.key}`)"
              :aria-label="qualityRingAriaLabel(ring)"
              :color="tokens.palette[0]"
              :track-color="tokens.gridLine"
              :data-testid="`insights-reviews-ring-${ring.key}`"
            />
          </div>
        </InsightsDataState>
        <div
          class="mt-4 flex items-start gap-2.5 rounded-2xl border border-subtle bg-surface-elevation-1 px-3.5 py-3"
          data-testid="insights-reviews-quality-readme"
        >
          <InfoIcon class="mt-0.5 lucide-icon-s shrink-0 text-icon-primary" aria-hidden="true" />
          <p class="max-w-prose txt-body-s-regular text-pretty text-subtle">{{ t('Reviews.product.insights.reviews.quality.info') }}</p>
        </div>
        <template #footer>
          <RouterLink
            class="btn-primary-color-s"
            :to="campaignsRoute"
            data-testid="insights-reviews-quality-cta"
            @click="trackCampaignsCta('insights_reviews_quality')"
          >
            {{ t('Reviews.product.insights.reviews.quality.cta') }}
            <ArrowRightIcon class="lucide-icon-s" aria-hidden="true" />
          </RouterLink>
        </template>
      </InsightsMetricCard>

      <InsightsMetricCard
        :headline="t('Reviews.product.insights.reviews.aspects.title')"
        :subheadline="t('Reviews.product.insights.reviews.aspects.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.aspects.tooltip')"
        data-testid="insights-reviews-card-aspects"
      >
        <InsightsDataState :loading="insightsLoading.aspects" :state="aspects.state" data-testid="insights-reviews-aspects">
          <ul class="grid grid-cols-2 gap-x-4 gap-y-6" data-testid="insights-reviews-aspects-chart">
            <li
              v-for="row in aspects.rows"
              :key="row.key"
              class="flex flex-col items-center gap-1 text-center"
              :data-testid="`insights-reviews-aspect-${kebabCase(row.key)}`"
            >
              <ArcProgress
                :progress="row.percent ?? 0"
                :status="row.percent == null ? 'disabled' : aspectStatus(row.percent)"
                :show-percentage="row.percent != null"
              />
              <span class="txt-label-m-semibold text-main">{{ t(`Reviews.product.insights.reviews.aspects.${row.key}`) }}</span>
              <span
                v-if="row.categoryPercent != null"
                class="txt-label-s-regular text-subtle"
                :data-testid="`insights-reviews-aspect-${kebabCase(row.key)}-category`"
              >
                {{ t('Reviews.product.insights.reviews.aspects.categoryAverage', { percent: formatPercent(row.categoryPercent / 100) }) }}
              </span>
              <InsightsTrendBadge
                v-if="row.delta != null"
                :value="t('Reviews.product.insights.reviews.aspects.delta', { value: formatSigned(row.delta) })"
                :direction="row.delta > 0 ? 'up' : row.delta < 0 ? 'down' : 'flat'"
                :data-testid="`insights-reviews-aspect-${kebabCase(row.key)}-delta`"
              />
            </li>
          </ul>
          <p class="mt-4 txt-label-s-regular text-subtle" data-testid="insights-reviews-aspects-annotation">
            {{ t('Reviews.product.insights.reviews.aspects.annotation') }}
          </p>
        </InsightsDataState>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.reviews.byCategory.title')"
        :subheadline="t('Reviews.product.insights.reviews.byCategory.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.byCategory.tooltip')"
        data-testid="insights-reviews-card-by-category"
      >
        <InsightsDataState
          :loading="insightsLoading.byCategory"
          :state="insights.byCategory.state"
          data-testid="insights-reviews-by-category"
        >
          <ul v-if="tokens" class="flex flex-col gap-3" data-testid="insights-reviews-by-category-chart">
            <li
              v-for="(row, index) in insights.byCategory.rows"
              :key="row.label"
              class="flex items-center gap-3.5"
              :data-testid="`insights-reviews-by-category-row-${index}`"
            >
              <span class="w-48 shrink-0 truncate txt-label-m-semibold text-main" :title="row.label">{{ row.label }}</span>
              <InsightsTrackBar
                class="h-4 min-w-0 flex-1"
                :value="row.value"
                :max="byCategoryMax"
                :color="row.label === insights.byCategory.weakest ? tokens.warning : tokens.palette[0]"
                :track-color="tokens.gridLine"
                :data-testid="`insights-reviews-by-category-row-${index}-bar`"
              />
              <span
                class="min-w-24 text-right txt-label-m-bold text-subtle tabular-nums"
                :data-testid="`insights-reviews-by-category-row-${index}-value`"
              >
                {{ formatCount(row.value) }}
              </span>
            </li>
          </ul>
        </InsightsDataState>
        <div
          v-if="insights.byCategory.weakest"
          class="mt-4 flex items-start gap-2.5 rounded-2xl border border-subtle bg-surface-elevation-1 px-3.5 py-3"
          data-testid="insights-reviews-by-category-readme"
        >
          <InfoIcon class="mt-0.5 lucide-icon-s shrink-0 text-icon-primary" aria-hidden="true" />
          <i18n-t
            keypath="Reviews.product.insights.reviews.byCategory.readme"
            tag="p"
            scope="global"
            class="max-w-prose txt-body-s-regular text-pretty text-subtle"
          >
            <template #name>
              <strong class="text-main" data-testid="insights-reviews-by-category-weakest">{{ insights.byCategory.weakest }}</strong>
            </template>
          </i18n-t>
        </div>
        <template #footer>
          <RouterLink
            class="btn-primary-color-s"
            :to="campaignsRoute"
            data-testid="insights-reviews-by-category-cta"
            @click="trackCampaignsCta('insights_reviews_by_category_campaigns')"
          >
            {{ t('Reviews.product.insights.reviews.byCategory.cta') }}
            <ArrowRightIcon class="lucide-icon-s" aria-hidden="true" />
          </RouterLink>
        </template>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.reviews.bySegment.label')"
        data-testid="insights-reviews-card-by-segment"
      >
        <template #badge>
          <RichSelectField
            class="w-56"
            size="s"
            :aria-label="t('Reviews.product.insights.reviews.bySegment.label')"
            :model-value="segment"
            data-testid="insights-reviews-segment-select"
            @update:model-value="onSegmentSelect"
          >
            <RichSelectOption
              v-for="dimension in SEGMENT_DIMENSIONS"
              :key="dimension"
              :value="dimension"
              :label="t(`Reviews.product.insights.reviews.bySegment.dimensions.${dimension}`)"
              :data-testid="`insights-reviews-segment-option-${dimension}`"
            />
          </RichSelectField>
        </template>
        <div class="grid grid-cols-1 gap-6 desktop:grid-cols-2">
          <InsightsMetricCard
            flat
            :headline="t('Reviews.product.insights.reviews.bySegment.distribution.title')"
            :subheadline="t('Reviews.product.insights.reviews.bySegment.distribution.subtitle', { segment: segmentInText })"
            :tooltip="t('Reviews.product.insights.reviews.bySegment.distribution.tooltip')"
            data-testid="insights-reviews-card-reviewer-distribution"
          >
            <InsightsDataState
              :loading="insightsLoading.bySegment"
              :state="segmentRows.shareState"
              :data-testid="`insights-reviews-${segmentId}`"
            >
              <template v-if="tokens">
                <ul class="flex flex-col gap-3" :data-testid="`insights-reviews-${segmentId}-chart`">
                  <li
                    v-for="row in segmentRows.rows"
                    :key="row.key"
                    class="flex items-center gap-3.5"
                    :data-testid="`insights-reviews-${segmentId}-row-${kebabCase(row.key)}`"
                  >
                    <span class="w-40 shrink-0 truncate txt-label-m-semibold text-main" :title="segmentLabel(row)">{{
                      segmentLabel(row)
                    }}</span>
                    <InsightsTrackBar
                      class="h-4 min-w-0 flex-1"
                      :value="row.share ?? 0"
                      :max="1"
                      :marker="row.categoryShare"
                      :marker-color="tokens.dataLabel"
                      :marker-label="shareMarkerLabel(row)"
                      :color="tokens.palette[0]"
                      :track-color="tokens.gridLine"
                      :data-testid="`insights-reviews-${segmentId}-row-${kebabCase(row.key)}-bar`"
                    />
                    <span
                      class="min-w-12 text-right"
                      :class="row.share == null ? 'txt-label-s-regular text-subtlest' : 'txt-label-m-bold text-subtle tabular-nums'"
                      :data-testid="`insights-reviews-${segmentId}-row-${kebabCase(row.key)}-value`"
                    >
                      {{ row.share == null ? t('Reviews.product.insights.reviews.notEnoughReviews') : formatPercent(row.share) }}
                    </span>
                  </li>
                </ul>
                <div class="mt-4 flex gap-4 txt-label-s-regular text-subtle" :data-testid="`insights-reviews-${segmentId}-legend`">
                  <span class="flex items-center gap-1.5">
                    <svg width="12" height="12" aria-hidden="true"><rect width="12" height="12" rx="2" :fill="tokens.palette[0]" /></svg>
                    {{ t('Reviews.product.insights.reviews.legend.you') }}
                  </span>
                  <span class="flex items-center gap-1.5">
                    <svg width="12" height="12" aria-hidden="true"><rect x="5" width="2" height="12" :fill="tokens.dataLabel" /></svg>
                    {{ t('Reviews.product.insights.reviews.legend.categoryAverage') }}
                  </span>
                </div>
              </template>
            </InsightsDataState>
          </InsightsMetricCard>

          <InsightsMetricCard
            flat
            :headline="t('Reviews.product.insights.reviews.bySegment.nps.title')"
            :subheadline="t('Reviews.product.insights.reviews.bySegment.nps.subtitle', { segment: segmentInText })"
            :tooltip="t('Reviews.product.insights.reviews.bySegment.nps.tooltip')"
            data-testid="insights-reviews-card-nps"
          >
            <InsightsDataState :loading="insightsLoading.bySegment" :state="segmentRows.npsState" data-testid="insights-reviews-nps">
              <template v-if="tokens">
                <ul class="flex flex-col gap-3" data-testid="insights-reviews-nps-chart">
                  <li
                    v-for="row in segmentRows.rows"
                    :key="row.key"
                    class="flex items-center gap-3.5"
                    :data-testid="`insights-reviews-nps-row-${kebabCase(row.key)}`"
                  >
                    <span class="w-40 shrink-0 truncate txt-label-m-semibold text-main" :title="segmentLabel(row)">{{
                      segmentLabel(row)
                    }}</span>
                    <InsightsDivergingBar
                      class="h-4 min-w-0 flex-1"
                      :value="row.nps ?? 0"
                      :positive-color="tokens.success"
                      :negative-color="tokens.error"
                      :track-color="tokens.gridLine"
                      :zero-color="tokens.axisLine"
                      :data-testid="`insights-reviews-nps-row-${kebabCase(row.key)}-bar`"
                    />
                    <span
                      class="min-w-12 text-right"
                      :class="
                        row.nps == null ? 'txt-label-s-regular text-subtlest' : ['txt-label-m-bold tabular-nums', scoreColorClass(row.nps)]
                      "
                      :data-testid="`insights-reviews-nps-row-${kebabCase(row.key)}-value`"
                    >
                      {{ row.nps == null ? t('Reviews.product.insights.reviews.notEnoughReviews') : formatSigned(row.nps) }}
                    </span>
                  </li>
                </ul>
              </template>
            </InsightsDataState>
          </InsightsMetricCard>
        </div>
      </InsightsMetricCard>

      <InsightsMetricCard
        v-if="!insightsLoading.switchedFrom && switches.state !== 'empty'"
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.reviews.switchedFrom.title')"
        :subheadline="t(`Reviews.product.insights.reviews.switchedFrom.${switches.scope === 'lifetime' ? 'subtitleLifetime' : 'subtitle'}`)"
        :tooltip="t('Reviews.product.insights.reviews.switchedFrom.tooltip', { tool: productTitle })"
        data-testid="insights-reviews-card-switched-from"
      >
        <InsightsDataState :loading="false" :state="switches.state" data-testid="insights-reviews-switched-from">
          <p
            v-if="switches.switchShare != null"
            class="mb-4 txt-body-m-regular text-main"
            data-testid="insights-reviews-switched-from-share"
          >
            {{
              t('Reviews.product.insights.reviews.switchedFrom.share', { share: formatPercent(switches.switchShare), tool: productTitle })
            }}
          </p>
          <ul v-if="tokens" class="flex flex-col gap-3" data-testid="insights-reviews-switched-from-chart">
            <li
              v-for="(row, index) in switches.rows"
              :key="row.key"
              class="flex items-center gap-3.5"
              :data-testid="`insights-reviews-switched-from-row-${index}`"
            >
              <span class="flex w-48 shrink-0 items-center gap-2 txt-label-m-semibold text-main" :title="switchLabel(row)">
                <ProductLogo v-if="row.key !== 'other'" :sources="{ 1: row.logoUrl ?? PLACEHOLDER_LOGO }" size="small" />
                <span class="truncate">{{ switchLabel(row) }}</span>
              </span>
              <InsightsTrackBar
                class="h-4 min-w-0 flex-1"
                :value="row.reviewers"
                :max="switchesMax"
                :color="row.key === 'other' ? tokens.dataLabel : tokens.palette[0]"
                :track-color="tokens.gridLine"
                :data-testid="`insights-reviews-switched-from-row-${index}-bar`"
              />
              <span
                class="min-w-24 text-right txt-label-m-bold text-subtle tabular-nums"
                :data-testid="`insights-reviews-switched-from-row-${index}-value`"
              >
                {{ formatCount(row.reviewers) }}
              </span>
            </li>
          </ul>
        </InsightsDataState>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.reviews.sentiment.title')"
        :subheadline="t('Reviews.product.insights.reviews.sentiment.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.sentiment.tooltip')"
        data-testid="insights-reviews-card-sentiment"
      >
        <InsightsDataState :loading="insightsLoading.sentiment" :state="insights.sentiment.state" data-testid="insights-reviews-sentiment">
          <div v-if="hasSentimentChips" class="mb-4" data-testid="insights-reviews-sentiment-chips">
            <p class="txt-label-s-semibold text-subtle">{{ t('Reviews.product.insights.reviews.sentiment.chips.caption') }}</p>
            <template v-for="group in SENTIMENT_CHIP_GROUPS" :key="group.key">
              <ul
                v-if="insights.sentiment.chips[group.key].length"
                class="mt-2 flex flex-wrap gap-2"
                :data-testid="`insights-reviews-sentiment-chips-${group.key}`"
              >
                <li v-for="chip in insights.sentiment.chips[group.key]" :key="chip.key">
                  <ChipBadge
                    :color="group.color"
                    rounded
                    size="s"
                    :data-testid="`insights-reviews-sentiment-chip-${kebabCase(chip.key)}-${group.key}`"
                  >
                    {{ sentimentChipLabel(chip) }}
                  </ChipBadge>
                </li>
              </ul>
            </template>
          </div>
          <template v-if="tokens">
            <p class="txt-label-s-semibold text-subtle" data-testid="insights-reviews-sentiment-scale">
              {{ t('Reviews.product.insights.reviews.sentiment.scale') }}
            </p>
            <ul class="mt-3 flex flex-col gap-3" data-testid="insights-reviews-sentiment-chart">
              <li
                v-for="row in insights.sentiment.rows"
                :key="row.key"
                class="flex items-center gap-3.5"
                :data-testid="`insights-reviews-sentiment-row-${kebabCase(row.key)}`"
              >
                <span class="w-48 shrink-0 truncate txt-label-m-semibold text-main">
                  {{ t(`Reviews.product.insights.reviews.attributes.${camelCase(row.key)}`) }}
                </span>
                <InsightsDivergingBar
                  class="h-4 min-w-0 flex-1"
                  :value="row.score"
                  :positive-color="tokens.success"
                  :negative-color="tokens.error"
                  :track-color="tokens.gridLine"
                  :zero-color="tokens.axisLine"
                  :data-testid="`insights-reviews-sentiment-row-${kebabCase(row.key)}-bar`"
                />
                <span
                  class="min-w-24 text-right txt-label-m-bold tabular-nums"
                  :class="scoreColorClass(row.score)"
                  :data-testid="`insights-reviews-sentiment-row-${kebabCase(row.key)}-value`"
                >
                  {{ formatSigned(row.score) }}
                </span>
              </li>
            </ul>
          </template>
        </InsightsDataState>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.reviews.campaigns.title')"
        :subheadline="t('Reviews.product.insights.reviews.campaigns.subtitle')"
        :tooltip="t('Reviews.product.insights.reviews.campaigns.tooltip')"
        data-testid="insights-reviews-card-campaigns"
      >
        <InsightsDataState :loading="insightsLoading.campaigns" :state="insights.campaigns.state" data-testid="insights-reviews-campaigns">
          <div class="overflow-x-auto">
            <TableLight class="w-full" data-testid="insights-reviews-campaigns-table">
              <thead>
                <tr>
                  <TableH
                    v-for="column in CAMPAIGN_COLUMNS"
                    :key="column"
                    :label="t(`Reviews.product.insights.reviews.campaigns.table.${column}`)"
                  />
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in insights.campaigns.rows" :key="row.id" :data-testid="`insights-reviews-campaigns-row-${row.id}`">
                  <td>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="txt-label-m-semibold text-main" :data-testid="`insights-reviews-campaigns-row-${row.id}-title`">
                        {{ row.title }}
                      </span>
                      <CampaignStatusBadge v-if="row.status" :status="row.status" />
                    </div>
                  </td>
                  <td class="whitespace-nowrap" :data-testid="`insights-reviews-campaigns-row-${row.id}-period`">
                    {{ formatDate(row.validFrom) }}<template v-if="row.validTo"> – {{ formatDate(row.validTo) }}</template>
                  </td>
                  <td class="tabular-nums" :data-testid="`insights-reviews-campaigns-row-${row.id}-started`">
                    {{ formatCount(row.started) }}
                  </td>
                  <td class="tabular-nums" :data-testid="`insights-reviews-campaigns-row-${row.id}-submitted`">
                    {{ formatCount(row.submitted) }}
                  </td>
                  <td class="tabular-nums" :data-testid="`insights-reviews-campaigns-row-${row.id}-published`">
                    {{ formatCount(row.published) }}
                  </td>
                  <td :data-testid="`insights-reviews-campaigns-row-${row.id}-conversion`">
                    <ChipBadge v-if="row.rate != null" :color="row.rate >= STRONG_CONVERSION ? 'success' : 'default'" rounded size="s">
                      {{ formatPercent(row.rate) }}
                    </ChipBadge>
                    <span v-else class="text-subtlest">—</span>
                  </td>
                </tr>
              </tbody>
            </TableLight>
          </div>
        </InsightsDataState>
        <template #footer>
          <RouterLink
            class="btn-primary-color-s"
            :to="campaignsRoute"
            data-testid="insights-reviews-campaigns-cta"
            @click="trackCampaignsCta('insights_reviews_campaigns_conversion')"
          >
            {{ t('Reviews.product.insights.reviews.campaigns.cta') }}
            <ArrowRightIcon class="lucide-icon-s" aria-hidden="true" />
          </RouterLink>
        </template>
      </InsightsMetricCard>
    </div>

    <div
      class="mt-6 flex items-start gap-2.5 rounded-2xl border border-subtle bg-surface-elevation-1 px-3.5 py-3"
      data-testid="insights-reviews-how-to-read"
    >
      <InfoIcon class="mt-0.5 lucide-icon-s shrink-0 text-icon-primary" aria-hidden="true" />
      <MarkdownContent
        unstyled
        class="max-w-prose txt-body-s-regular text-pretty text-subtle [&_strong]:text-main"
        :md="t('Reviews.product.insights.reviews.howToRead.text')"
        data-testid="insights-reviews-how-to-read-text"
      />
    </div>
  </section>
</template>

<script lang="ts">
const NOT_LOADING: ReviewsInsightsLoading = {
  distribution: false,
  aspects: false,
  quality: false,
  totalReviews: false,
  newReviews: false,
  badge: false,
  byCategory: false,
  bySegment: false,
  sentiment: false,
  history: false,
  campaigns: false,
  switchedFrom: false,
}

const PLACEHOLDER_LOGO = '/assets/images/placeholder-logo.svg'

const CAMPAIGN_COLUMNS = ['campaign', 'period', 'started', 'submitted', 'published', 'conversion'] as const

const STRONG_CONVERSION = 0.5

const SEGMENT_DIMENSIONS: ReviewsSegmentDimension[] = ['seniority', 'industry', 'companySize']
const TRANSLATED_INDUSTRIES = new Set([
  'marketingAndAdvertising',
  'informationTechnology',
  'computerSoftware',
  'construction',
  'realEstate',
])
</script>

<script setup lang="ts">
import { ArcProgress, ChipBadge, MarkdownContent, RatingStars, RichSelectField, RichSelectOption } from '@ramp106/omrjs-core-ui'
import { ArrowRightIcon, InfoIcon, StarIcon } from '@ramp106/omrjs-core-ui/icons'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { camelCase, kebabCase } from 'lodash-es'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CampaignStatusBadge from '@/components/ReviewsManagement/Reviews/CampaignStatusBadge.vue'
import { useFormatTableDate } from '@/components/ReviewsManagement/Reviews/useFormatTableDate'
import { useOmrEchartsTheme } from '@/composables/useOmrEchartsTheme'
import type {
  ReviewsAspects,
  ReviewsDistribution,
  ReviewsInsightsLoading,
  ReviewsInsightsView,
  ReviewsOwnInsightsView,
  ReviewsQualityRing,
  ReviewsScoreHistory,
  ReviewsSegmentDimension,
  ReviewsSegmentRow,
  ReviewsSentimentChip,
  ReviewsSwitches,
  ReviewsSwitchRow,
} from '@/gql/reviews/mappers/insights'
import { makeNumber, makeRatioPercent, makeSigned } from '@/helpers/chartFormat'
import { ReviewsRouteName } from '@/router/types'
import { TableH, TableLight } from '@/ui/TableLight'

import { useRouterQueryInsightsTimeframe } from '../useRouterQueryInsightsTimeframe'

const props = withDefaults(
  defineProps<{
    view: ReviewsOwnInsightsView
    distribution: ReviewsDistribution
    aspects: ReviewsAspects
    history: ReviewsScoreHistory
    insights: ReviewsInsightsView
    switches: ReviewsSwitches
    productId: string
    productTitle: string
    insightsLoading?: ReviewsInsightsLoading
  }>(),
  { insightsLoading: () => NOT_LOADING },
)

const { t, locale } = useI18n()
const { rootRef, tokens } = useOmrEchartsTheme()
const { timeframe } = useRouterQueryInsightsTimeframe()

const shareOf = (value: number): number => (props.distribution.total ? value / props.distribution.total : 0)

const lifetimeShare = (stars: number): number | null => {
  const { rows, total } = props.view.lifetimeDistribution
  const row = rows.find((item) => item.stars === stars)
  return row && total ? row.value / total : null
}

const lifetimeMarkerLabel = (stars: number): string | undefined => {
  const share = lifetimeShare(stars)
  return share == null ? undefined : t('Reviews.product.insights.reviews.distribution.markerTooltip', { share: formatPercent.value(share) })
}

const distributionReadme = computed(() => {
  const best = props.distribution.rows.find((row) => row.stars === 5)
  const lifetime = lifetimeShare(5)
  if (!best || !props.distribution.total || lifetime == null) return null
  const share = shareOf(best.value)
  const delta = Math.round(share * 100) - Math.round(lifetime * 100)
  const params = {
    period: timeframeLabel.value,
    share: formatPercent.value(share),
    lifetime: formatPercent.value(lifetime),
    delta: Math.abs(delta),
  }
  const key = delta > 0 ? 'above' : delta < 0 ? 'below' : 'same'
  return t(`Reviews.product.insights.reviews.distribution.readme.${key}`, params)
})

const hoveredWeek = ref<number | null>(null)

watch(
  () => props.history,
  () => {
    hoveredWeek.value = null
  },
)

const scoreHero = computed(() => {
  const index = hoveredWeek.value
  const point = index == null ? undefined : props.history.points[index]
  if (index != null && point) {
    const week = props.history.weeks[index]
    return { ...point, caption: point.trend ? t('Reviews.product.insights.reviews.yourScore.captionHover', { week }) : week }
  }
  return {
    value: props.view.score.ratingLabel,
    trend: props.history.trend,
    caption: t('Reviews.product.insights.reviews.yourScore.caption'),
  }
})

const scoreCount = computed(() => {
  const index = hoveredWeek.value
  const point = index == null ? undefined : props.history.points[index]
  const value = point ? point.reviews : props.view.score.totalCount
  return { value, label: point ? formatCount.value(value) : props.view.score.total }
})

const aspectStatus = (percent: number): 'success' | 'warning' | 'error' => (percent >= 70 ? 'success' : percent >= 50 ? 'warning' : 'error')

const qualityRingAriaLabel = (ring: ReviewsQualityRing): string | undefined =>
  ring.value == null
    ? `${t(`Reviews.product.insights.reviews.quality.${ring.key}`)}: ${t('Reviews.product.insights.state.noData')}`
    : undefined

const timeframeLabel = computed(() => t(`Reviews.product.insights.filters.timeframe.options.${timeframe.value}`))

const reviewCountBenchmarkCategories = computed(() => [
  t('Reviews.product.insights.reviews.reviewCount.benchmark.you'),
  t('Reviews.product.insights.reviews.reviewCount.benchmark.top3'),
  t('Reviews.product.insights.reviews.reviewCount.benchmark.top10'),
  t('Reviews.product.insights.reviews.reviewCount.benchmark.categoryAvg'),
])

const reviewCountBenchmarkValues = computed(() => {
  const { totalReviews, benchmark } = props.insights.timeframe
  return [totalReviews.count, benchmark.top3, benchmark.top10, benchmark.categoryAvg]
})

const reviewCountMedianLabel = computed(() => {
  const { median } = props.insights.timeframe.benchmark
  return median == null
    ? undefined
    : t('Reviews.product.insights.reviews.reviewCount.benchmark.median', { value: formatCount.value(median) })
})

const currentQuarter = (() => {
  const today = new Date()
  return `Q${Math.floor(today.getMonth() / 3) + 1} / ${today.getFullYear()}`
})()

const campaignsRoute = computed(() => ({
  name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns,
  params: { productId: props.productId },
}))

const trackCampaignsCta = (ctaId: string) => {
  track('cta_click', { surface: 'cta', category: 'meaningful_action', cta_id: ctaId })
}

const formatDate = useFormatTableDate()

const formatCount = computed(() => makeNumber(locale.value))

const byCategoryMax = computed(() => Math.max(...props.insights.byCategory.rows.map((row) => row.value)))

const formatPercent = computed(() => makeRatioPercent(locale.value, 0))

const formatSigned = computed(() => makeSigned(locale.value))

const scoreColorClass = (score: number): string => (score >= 0 ? 'text-success' : 'text-error')

const SENTIMENT_CHIP_GROUPS = [
  { key: 'positive', color: 'success' },
  { key: 'negative', color: 'error' },
] as const
const hasSentimentChips = computed(() => SENTIMENT_CHIP_GROUPS.some(({ key }) => props.insights.sentiment.chips[key].length > 0))
const sentimentChipLabel = ({ key, share }: ReviewsSentimentChip): string =>
  t('Reviews.product.insights.reviews.sentiment.chips.chip', {
    share,
    label: t(`Reviews.product.insights.reviews.attributes.${camelCase(key)}`),
  })

const segment = ref<ReviewsSegmentDimension>('seniority')

const segmentId = computed(() => kebabCase(segment.value))

const segmentInText = computed(() => t(`Reviews.product.insights.reviews.bySegment.inText.${segment.value}`))

const segmentRows = computed(() => props.insights.bySegment[segment.value])

const onSegmentSelect = (value: string | string[]) => {
  const dimension = SEGMENT_DIMENSIONS.find((candidate) => candidate === value)
  if (dimension) segment.value = dimension
}

const shareMarkerLabel = (row: ReviewsSegmentRow): string | undefined =>
  row.categoryShare == null
    ? undefined
    : t('Reviews.product.insights.reviews.bySegment.distribution.markerTooltip', { share: formatPercent.value(row.categoryShare) })

const switchesMax = computed(() => Math.max(...props.switches.rows.map((row) => row.reviewers)))

const switchLabel = (row: ReviewsSwitchRow): string =>
  row.key === 'other' ? t('Reviews.product.insights.reviews.switchedFrom.otherTools') : (row.label ?? row.key)

const segmentLabel = (row: ReviewsSegmentRow): string => {
  if (row.other) return t('Reviews.product.insights.reviews.industry.other')
  if (segment.value !== 'industry') return t(`Reviews.product.insights.reviews.${segment.value}.${row.key}`)
  return TRANSLATED_INDUSTRIES.has(row.key) ? t(`Reviews.product.insights.reviews.industry.names.${row.key}`) : (row.label ?? '')
}
</script>
